import { useState, useEffect } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Check, TrendingUp, Clock, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { maintenanceAPI } from "../../services/maintenance/api";

interface ProveedorSelectorProps {
  itemId: number;
  itemNombre: string;
  onSelect: (proveedorId: number, precio: number) => void;
  proveedorPreseleccionado?: number;
}

export function ProveedorSelector({
  itemId,
  itemNombre,
  onSelect,
  proveedorPreseleccionado,
}: ProveedorSelectorProps) {
  const [open, setOpen] = useState(false);
  const [cotizaciones, setCotizaciones] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(
    proveedorPreseleccionado || null,
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    const fetchCotizaciones = async () => {
      try {
        setLoading(true);

        console.log("🔍 Fetching cotizaciones para item:", itemId);

        const response = await maintenanceAPI.get(
          `/proveedores/cotizaciones/${itemId}`,
        );

        console.log("✅ Response completa:", response);
        console.log("✅ Cotizaciones:", response.data);

        setCotizaciones(response.data.data || []);
      } catch (error: any) {
        console.error("❌ Error fetching cotizaciones:", error);
        console.error("❌ Error response:", error.response?.data);
        console.error("❌ Error status:", error.response?.status);
        setCotizaciones([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCotizaciones();
  }, [open, itemId]);

  const handleSelect = (cotizacion: any, e?: React.MouseEvent) => {
    // Prevenir comportamiento por defecto y propagación
    e?.preventDefault();
    e?.stopPropagation();

    console.log("🎯 ===== SELECCIÓN DE PROVEEDOR =====");
    console.log("Cotización completa:", cotizacion);
    console.log("Proveedor ID:", cotizacion.proveedor.id);
    console.log("Precio:", cotizacion.precio_unitario);

    setSelectedId(cotizacion.proveedor.id);

    console.log("Ejecutando callback onSelect...");
    onSelect(cotizacion.proveedor.id, cotizacion.precio_unitario);

    console.log("Cerrando modal...");
    setOpen(false);

    console.log("✅ Selección completada");
  };

  const getCalificacionColor = (cal: string) => {
    switch (cal) {
      case "A":
        return "bg-green-100 text-green-700 border-green-200";
      case "B":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "C":
        return "bg-orange-100 text-orange-700 border-orange-200";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className="w-full justify-between"
      >
        <span className="truncate">
          {selectedId ? "Proveedor Seleccionado" : "Seleccionar Proveedor"}
        </span>
        <Star className="h-4 w-4 ml-2" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Seleccionar Proveedor - {itemNombre}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-8 text-gray-500">
                Cargando proveedores...
              </div>
            ) : cotizaciones.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay cotizaciones vigentes para este ítem
              </div>
            ) : (
              cotizaciones.map((cot) => (
                <div
                  key={cot.id}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    cot.es_preferido
                      ? "border-green-300 bg-green-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                  onClick={(e) => handleSelect(cot, e)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleSelect(cot);
                    }
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-bold text-gray-900">
                          {cot.proveedor.razon_social}
                        </h4>
                        <Badge
                          className={getCalificacionColor(
                            cot.proveedor.calificacion,
                          )}
                        >
                          Nivel {cot.proveedor.calificacion}
                        </Badge>
                        {cot.es_preferido && (
                          <Badge className="bg-green-100 text-green-700">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            Preferido
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <div className="bg-white p-2 rounded">
                          <span className="text-xs text-gray-500">Precio</span>
                          <p className="font-bold text-blue-600">
                            S/ {cot.precio_unitario}
                          </p>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <span className="text-xs text-gray-500 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            Entrega
                          </span>
                          <p className="font-medium">
                            {cot.tiempo_entrega_dias}d
                          </p>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <span className="text-xs text-gray-500">
                            Puntualidad
                          </span>
                          <p className="font-medium">
                            {cot.metricas_proveedor?.porcentaje_puntualidad?.toFixed(
                              0,
                            ) || 0}
                            %
                          </p>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <span className="text-xs text-gray-500 flex items-center">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Score
                          </span>
                          <p className="font-bold text-green-600">
                            {cot.score_total || 0}
                          </p>
                        </div>
                      </div>

                      {cot.metricas_proveedor?.total_pedidos > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          {cot.metricas_proveedor.total_pedidos} pedidos en
                          últimos 6 meses
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Check className="h-5 w-5 text-blue-600" />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
